import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useToastNotification } from '@/contexts/ToastContext';
import {
  Plus,
  Search,
  Filter,
  Mail,
  Phone,
  Calendar as CalendarIcon,
  DollarSign,
  FileText,
  ArrowDownRight,
  ArrowUpRight,
  X,
  Pencil,
  Trash2,
  User,
  Clock,
  ExternalLink,
  ChevronDown
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CalendarSingle } from '@/components/ui/calendar-single';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTERACTION_TYPES = [
  { value: 'email', label: 'Email', icon: Mail, color: 'bg-blue-500' },
  { value: 'call', label: 'Call', icon: Phone, color: 'bg-green-500' },
  { value: 'meeting', label: 'Meeting', icon: CalendarIcon, color: 'bg-purple-500' },
  { value: 'transaction', label: 'Transaction', icon: DollarSign, color: 'bg-yellow-500' },
  { value: 'note', label: 'Note', icon: FileText, color: 'bg-gray-500' }
];

const DIRECTIONS = [
  { value: 'inbound', label: 'Inbound', icon: ArrowDownRight },
  { value: 'outbound', label: 'Outbound', icon: ArrowUpRight }
];

function InteractionLogging() {
  const toast = useToastNotification();
  const [interactions, setInteractions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDirection, setFilterDirection] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [formData, setFormData] = useState({
    contact_id: 'none',
    type: 'email',
    direction: 'inbound',
    channel: '',
    subject: '',
    description: '',
    timestamp: new Date()
  });

  // Fetch interactions
  const fetchInteractions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterDirection !== 'all') params.append('direction', filterDirection);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API_URL}/api/interactions?${params}`, {
        withCredentials: true
      });
      setInteractions(response.data);
    } catch (error) {
      console.error('Failed to fetch interactions:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterDirection, searchQuery]);

  // Fetch contacts for dropdown
  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contacts`, {
        withCredentials: true
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, []);

  useEffect(() => {
    fetchInteractions();
    fetchContacts();
  }, [fetchInteractions, fetchContacts]);

  // Create interaction
  const handleCreate = async () => {
    try {
      const dataToSend = {
        ...formData,
        contact_id: formData.contact_id === 'none' ? null : formData.contact_id,
        timestamp: formData.timestamp.toISOString()
      };
      
      await axios.post(
        `${API_URL}/api/interactions`,
        dataToSend,
        { withCredentials: true }
      );
      setShowCreateModal(false);
      resetForm();
      fetchInteractions();
      toast.success('Interaction logged');
    } catch (error) {
      console.error('Failed to create interaction:', error);
      toast.error('Failed to log interaction');
    }
  };

  // Update interaction
  const handleUpdate = async () => {
    try {
      const dataToSend = {
        ...formData,
        contact_id: formData.contact_id === 'none' ? null : formData.contact_id,
        timestamp: formData.timestamp.toISOString()
      };
      
      await axios.put(
        `${API_URL}/api/interactions/${selectedInteraction.id}`,
        dataToSend,
        { withCredentials: true }
      );
      setShowEditModal(false);
      setSelectedInteraction(null);
      resetForm();
      fetchInteractions();
      toast.success('Interaction updated');
    } catch (error) {
      console.error('Failed to update interaction:', error);
      toast.error('Failed to update interaction');
    }
  };

  // Delete interaction
  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/interactions/${selectedInteraction.id}`,
        { withCredentials: true }
      );
      setShowDeleteModal(false);
      setSelectedInteraction(null);
      fetchInteractions();
      toast.success('Interaction deleted');
    } catch (error) {
      console.error('Failed to delete interaction:', error);
      toast.error('Failed to delete interaction');
    }
  };

  const resetForm = () => {
    setFormData({
      contact_id: 'none',
      type: 'email',
      direction: 'inbound',
      channel: '',
      subject: '',
      description: '',
      timestamp: new Date()
    });
  };

  const openEditModal = (interaction) => {
    setSelectedInteraction(interaction);
    setFormData({
      contact_id: interaction.contact_id || 'none',
      type: interaction.type,
      direction: interaction.direction,
      channel: interaction.channel || '',
      subject: interaction.subject || '',
      description: interaction.description || '',
      timestamp: new Date(interaction.timestamp)
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (interaction) => {
    setSelectedInteraction(interaction);
    setShowDeleteModal(true);
  };

  const getTypeIcon = (type) => {
    const typeConfig = INTERACTION_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : FileText;
  };

  const getTypeColor = (type) => {
    const typeConfig = INTERACTION_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.color : 'bg-gray-500';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Interaction Logging</h1>
            <p className="text-gray-400">
              Track all client interactions - emails, calls, meetings, and transactions
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Log Interaction
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-[#0f1115] border border-white/10 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search interactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#111318] border-white/10 text-white"
              />
            </div>

            {/* Type Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-[#111318] border-white/10 text-white">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {INTERACTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Direction Filter */}
            <Select value={filterDirection} onValueChange={setFilterDirection}>
              <SelectTrigger className="bg-[#111318] border-white/10 text-white">
                <SelectValue placeholder="All Directions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Directions</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Interactions Timeline */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading interactions...</div>
          ) : interactions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-600 mb-4" />
              <p className="text-gray-500 mb-2">No interactions logged yet</p>
              <p className="text-sm text-gray-600">
                Start logging interactions to build client timelines
              </p>
            </div>
          ) : (
            interactions.map((interaction) => {
              const TypeIcon = getTypeIcon(interaction.type);
              const DirectionIcon = interaction.direction === 'inbound' ? ArrowDownRight : ArrowUpRight;

              return (
                <motion.div
                  key={interaction.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0f1115] border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`${getTypeColor(interaction.type)} p-2 rounded-lg`}>
                      <TypeIcon className="h-5 w-5 text-white" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-medium">
                              {interaction.subject || `${interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)} Interaction`}
                            </h3>
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <DirectionIcon className="h-3 w-3" />
                              {interaction.direction}
                            </span>
                          </div>
                          
                          {interaction.contact_name && (
                            <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                              <User className="h-4 w-4" />
                              {interaction.contact_name}
                              {interaction.contact_email && (
                                <span className="text-gray-500">• {interaction.contact_email}</span>
                              )}
                            </div>
                          )}

                          {interaction.description && (
                            <p className="text-gray-400 text-sm line-clamp-2">
                              {interaction.description}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(interaction)}
                            className="p-2 hover:bg-white/5 rounded transition-colors"
                          >
                            <Pencil className="h-4 w-4 text-gray-400" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(interaction)}
                            className="p-2 hover:bg-red-500/10 rounded transition-colors"
                          >
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </button>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTimestamp(interaction.timestamp)}
                        </span>
                        {interaction.channel && (
                          <span>via {interaction.channel}</span>
                        )}
                        <span>by {interaction.created_by_name}</span>
                        {interaction.source !== 'manual' && (
                          <span className="text-blue-400">• Auto-logged</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }
      }}>
        <DialogContent className="bg-[#0f1115] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {showEditModal ? 'Edit Interaction' : 'Log New Interaction'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Contact */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Contact (Optional)</label>
              <Select value={formData.contact_id || "none"} onValueChange={(value) => setFormData({ ...formData, contact_id: value === "none" ? "" : value })}>
                <SelectTrigger className="bg-[#111318] border-white/10">
                  <SelectValue placeholder="Select contact..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {contacts.map(contact => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.firstName} {contact.lastName} ({contact.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type & Direction */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Type</label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger className="bg-[#111318] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERACTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-2 block">Direction</label>
                <Select value={formData.direction} onValueChange={(value) => setFormData({ ...formData, direction: value })}>
                  <SelectTrigger className="bg-[#111318] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIRECTIONS.map(dir => (
                      <SelectItem key={dir.value} value={dir.value}>
                        {dir.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Channel */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Channel (Optional)</label>
              <Input
                placeholder="e.g., email, phone, zoom"
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                className="bg-[#111318] border-white/10"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Subject/Title</label>
              <Input
                placeholder="Brief title of the interaction"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="bg-[#111318] border-white/10"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Notes/Description</label>
              <Textarea
                placeholder="Add details about this interaction..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#111318] border-white/10 min-h-[100px]"
              />
            </div>

            {/* Timestamp */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Date & Time</label>
              <CalendarSingle
                date={formData.timestamp}
                onDateChange={(date) => setFormData({ ...formData, timestamp: date })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                resetForm();
              }}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={showEditModal ? handleUpdate : handleCreate}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {showEditModal ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#0f1115] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Interaction?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-400">
            Are you sure you want to delete this interaction? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default InteractionLogging;
